import { Calendar, Upload as UploadIcon, X } from 'lucide-react';
import { type ChangeEvent, useRef, useState } from 'react';

import { SelectMenu } from '@/components/patterns/SelectMenu';
import { cn } from '@/lib/utils';

import {
  addressSubfieldsOf,
  asAddress,
  asBool,
  asCascade,
  asChoices,
  asDateRange,
  asSingleChoice,
  asText,
  asUploads,
  type CascadeAnswer,
  cascadePathIds,
  optionsOf,
  type RenderableField,
  type RenderableSection,
  type SectionAnswers,
  type SectionErrors,
  type UploadedFile,
  uploadByteSize,
} from './answers';
import { type Cascade, cascadeNodesAt } from './fieldConfig';
import { elementFor } from './registry';

/**
 * THE questionnaire renderer. Draws one section's fields, for every element type.
 *
 * WHY THERE IS EXACTLY ONE OF THESE. The builder's phone preview and the applicant's live screen
 * are the same form. The reference implementation acknowledges this — its preview component takes
 * a `standalone` flag and doubles as the applicant page — and we keep that, because the
 * alternative is seventeen field renderers written twice. The pair would drift on the first
 * change, and the drift would be invisible: the preview would keep looking right while the real
 * screen diverged, which is the one failure mode a preview exists to prevent.
 *
 * It is a CONTROLLED component. It owns no answer state, so the builder can hand it throwaway
 * state and the runner can hand it state it submits, with no branch inside.
 *
 * Every element type is reached from `elementFor`'s catalogue, so a type added to the registry
 * cannot silently render as a bare text box here — `renderControl` ends in an explicit default
 * that is only correct for genuinely text-like types.
 */

export interface QuestionnaireRendererProps {
  section: RenderableSection;
  answers: SectionAnswers;
  onChange: (fieldId: string, value: unknown) => void;
  /** Field id → message. Shown under the control. */
  errors?: SectionErrors;
  /** Tenant logo above the heading. Omitted in the compact builder preview. */
  logoUrl?: string | null;
  /**
   * False for a read-only rendering (the admin's session view). Controls stay visible so the
   * answers are legible in context, but nothing can be edited.
   */
  interactive?: boolean;
  /** Shown when the section has no fields — the builder wants a hint, a live run never hits it. */
  emptyHint?: string;
  /**
   * Bytes already attached across EVERY upload field of the WHOLE step, including this section's.
   *
   * Passed in rather than derived here because the budget is per STEP and this component only ever
   * sees ONE section — a step submits all of its sections in one request, so the sum is what has to
   * fit. Only the caller holding every section's answers can compute it (see
   * `totalUploadBytes`). Omitted in the builder preview, where nothing is submitted.
   */
  stepUploadBytes?: number;
}

export function QuestionnaireRenderer({
  section,
  answers,
  onChange,
  errors,
  logoUrl,
  interactive = true,
  emptyHint,
  stepUploadBytes = 0,
}: QuestionnaireRendererProps) {
  return (
    <>
      <header className="mb-[clamp(18px,1.5vw,24px)] flex flex-col items-center text-center">
        {logoUrl ? (
          <img
            src={logoUrl}
            alt=""
            className="mb-[clamp(10px,0.8vw,12px)] max-h-10 max-w-[156px] object-contain"
          />
        ) : null}
        <h2
          className={cn(
            'm-0 mb-1 text-[length:var(--text-22)] font-semibold leading-tight',
            section.title.trim().length > 0 ? 'text-[#041A3A]' : 'text-placeholder',
          )}
        >
          {section.title.trim().length > 0 ? section.title : 'Section'}
        </h2>
        <p className="m-0 mb-[clamp(8px,0.85vw,10px)] w-full max-w-[304px] text-[length:var(--text-14)] leading-tight text-gray66">
          {section.description.trim().length > 0
            ? section.description
            : 'Please fill in the required details to complete this section'}
        </p>
      </header>

      {section.fields.length === 0 ? (
        <div className="mt-2 flex w-full items-center justify-center rounded-xl border-2 border-dashed border-[#E5E7EB] py-8 text-center text-[length:var(--text-12)] text-placeholder">
          {emptyHint ?? 'No fields added yet in this section'}
        </div>
      ) : (
        <div className="flex w-full flex-col gap-4">
          {section.fields.map((field) => (
            <FieldBlock
              key={field.id}
              field={field}
              answers={answers}
              onChange={onChange}
              error={errors?.[field.id]}
              interactive={interactive}
              stepUploadBytes={stepUploadBytes}
            />
          ))}
        </div>
      )}
    </>
  );
}

// ── One field ─────────────────────────────────────────────────────────────────

const INPUT_CLASS =
  'w-full rounded-lg border border-placeholder bg-white px-3 py-2 text-[14px] leading-normal text-[#323338] outline-none transition-colors placeholder:text-[#8E8E8E] focus:border-primary focus:ring-2 focus:ring-primary/10 disabled:cursor-default disabled:bg-[#F9FAFB]';

function FieldBlock({
  field,
  answers,
  onChange,
  error,
  interactive,
  stepUploadBytes,
}: {
  field: RenderableField;
  answers: SectionAnswers;
  onChange: (fieldId: string, value: unknown) => void;
  error?: string;
  interactive: boolean;
  /** See QuestionnaireRendererProps.stepUploadBytes — only the upload control reads it. */
  stepUploadBytes: number;
}) {
  // Consent carries its statement beside the checkbox, so a label above it would state the same
  // thing twice.
  //
  // A CASCADE draws its own label for every tier, including the first — its levels ARE the
  // questions. Rendering this label too would print "Country" twice above the same select whenever
  // the field's title and its first level agree, and would leave "Level 1" doing nothing whenever
  // they do not. One label per dropdown, sourced from the level; see `CascadeSelects`.
  const ownsItsLabels = field.fieldType === 'drop-down' && field.config.cascade !== undefined;
  const showLabel = field.fieldType !== 'consent-text' && !ownsItsLabels;
  const label =
    field.label.trim().length > 0 ? field.label : elementFor(field.fieldType).defaults.label;

  return (
    <div className="flex w-full flex-col text-left">
      {showLabel ? (
        <span className="mb-1.5 block text-[length:var(--text-14)] font-medium leading-snug text-[#041A3A]">
          {label.trim().length > 0 ? label : 'Question label'}
          {field.isMandatory ? <span className="ml-0.5 text-destructive">*</span> : null}
        </span>
      ) : null}

      {renderControl(field, answers, onChange, interactive, stepUploadBytes)}

      {/*
        The validation message is shown here ONLY when the field is in error. The reference
        renders it permanently under Number and Paragraph, in red, on a form nobody has touched —
        so an untouched questionnaire greets the applicant with what look like failures. It is an
        error message; it belongs to an error.
      */}
      {error ? (
        <span className="mt-1.5 block text-right text-[length:var(--text-12)] font-medium text-destructive">
          {error}
        </span>
      ) : null}
    </div>
  );
}

function renderControl(
  field: RenderableField,
  answers: SectionAnswers,
  onChange: (fieldId: string, value: unknown) => void,
  interactive: boolean,
  stepUploadBytes: number,
): React.ReactNode {
  const disabled = !interactive;
  const value = answers[field.id];
  const set = (next: unknown) => onChange(field.id, next);

  switch (field.fieldType) {
    case 'long-text':
    case 'paragraph':
      return (
        <textarea
          rows={field.fieldType === 'paragraph' ? 4 : 3}
          className={cn(INPUT_CLASS, 'resize-none py-2.5')}
          placeholder={field.placeholder}
          value={asText(value)}
          disabled={disabled}
          onChange={(e) => set(e.target.value)}
        />
      );

    case 'single-choice': {
      // A STRING, not a one-element array — see asSingleChoice. Clicking the chosen chip again clears
      // it, so an optional question can be un-answered without reloading the form.
      const chosen = asSingleChoice(value);
      return (
        <ChoiceChips
          options={optionsOf(field.config)}
          selected={chosen ? [chosen] : []}
          disabled={disabled}
          onToggle={(option) => set(option === chosen ? '' : option)}
        />
      );
    }

    case 'multiple-choice':
      return (
        <ChoiceChips
          options={optionsOf(field.config)}
          selected={asChoices(value)}
          disabled={disabled}
          onToggle={(option) => {
            const current = asChoices(value);
            set(
              current.includes(option) ? current.filter((o) => o !== option) : [...current, option],
            );
          }}
        />
      );

    case 'date':
      return (
        <DateInput
          value={asText(value)}
          disabled={disabled}
          onChange={set}
          placeholder={field.placeholder}
        />
      );

    case 'date-range': {
      const range = asDateRange(value);
      return (
        // Divs with an `ariaLabel` on each input, not <label> wrappers: the input lives inside
        // DateInput, so a wrapping label has no control it can be associated with as far as the
        // markup is concerned — and a label pointing at nothing is worse for a screen reader than
        // an explicit aria-label, which is what actually gets announced.
        <div className="grid w-full grid-cols-2 gap-2.5">
          <div className="flex flex-col gap-1 text-left">
            <span className="text-[12px] font-medium text-[#475569]">From</span>
            <DateInput
              value={range.from}
              disabled={disabled}
              onChange={(next) => set({ ...range, from: asText(next) })}
              ariaLabel={`${field.label || 'Date range'} — from`}
              compact
            />
          </div>
          <div className="flex flex-col gap-1 text-left">
            <span className="text-[12px] font-medium text-[#475569]">To</span>
            <DateInput
              value={range.to}
              disabled={disabled}
              onChange={(next) => set({ ...range, to: asText(next) })}
              // A range cannot end before it starts, so the browser's own picker enforces it
              // instead of the applicant discovering it from an error after submitting.
              min={range.from || undefined}
              ariaLabel={`${field.label || 'Date range'} — to`}
              compact
            />
          </div>
        </div>
      );
    }

    case 'number':
      return (
        <input
          type="number"
          className={INPUT_CLASS}
          placeholder={field.placeholder}
          value={asText(value)}
          disabled={disabled}
          onChange={(e) => set(e.target.value)}
        />
      );

    case 'address': {
      const answer = asAddress(value);
      return (
        <div className="flex w-full flex-col gap-2.5 text-left">
          {addressSubfieldsOf(field.config).map((sub) => (
            <label key={sub.id} className="block w-full">
              <span className="mb-1 block text-[12px] font-medium text-[#475569]">{sub.title}</span>
              <input
                type="text"
                className={INPUT_CLASS}
                placeholder={sub.placeholder}
                value={answer[sub.id] ?? ''}
                disabled={disabled}
                onChange={(e) => set({ ...answer, [sub.id]: e.target.value })}
              />
            </label>
          ))}
        </div>
      );
    }

    case 'drop-down': {
      const { cascade } = field.config;
      if (cascade) {
        return (
          <CascadeSelects
            cascade={cascade}
            answer={asCascade(value)}
            // Used only as the FIRST tier's label when that level has no title of its own, and to
            // carry the mandatory marker that `FieldBlock` no longer prints for a cascade.
            fieldLabel={field.label}
            isMandatory={field.isMandatory}
            disabled={disabled}
            onChange={set}
          />
        );
      }

      // No cascade configured — a plain single dropdown over `options`.
      return <PlainSelect field={field} value={asText(value)} disabled={disabled} onChange={set} />;
    }

    // Retired from the palette but still rendered for questionnaires already saved with them —
    // see `RETIRED_ELEMENTS` in the registry.
    case 'state':
    case 'district':
    case 'city':
      return <PlainSelect field={field} value={asText(value)} disabled={disabled} onChange={set} />;

    case 'upload':
      return (
        <FileField
          files={asUploads(value)}
          label={field.placeholder || 'Upload document'}
          disabled={disabled}
          onChange={set}
          stepUploadBytes={stepUploadBytes}
        />
      );

    case 'consent-text':
      return (
        <label className="flex items-start gap-2 pt-1">
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4 shrink-0 rounded border-placeholder accent-primary"
            checked={asBool(value)}
            disabled={disabled}
            onChange={(e) => set(e.target.checked)}
          />
          <span className="text-left text-[12.5px] leading-tight text-[#323338]">
            {field.placeholder || 'I agree to the terms and conditions'}
            {field.isMandatory ? <span className="ml-0.5 text-destructive">*</span> : null}
          </span>
        </label>
      );

    // short-text, email, mobile-number, and any unrecognised type. Safe as a catch-all because
    // every element whose control is NOT a text box is handled explicitly above — a new registry
    // entry that needs something else will not silently land here unnoticed, it will look wrong
    // in the preview immediately.
    default:
      return (
        <input
          type={inputTypeFor(field.fieldType)}
          className={INPUT_CLASS}
          placeholder={field.placeholder}
          value={asText(value)}
          disabled={disabled}
          onChange={(e) => set(e.target.value)}
          // Surfaces the phone keypad / email keyboard on mobile, which is most of where this
          // form is filled in.
          inputMode={field.fieldType === 'mobile-number' ? 'tel' : undefined}
          autoComplete={autoCompleteFor(field.fieldType)}
        />
      );
  }
}

function inputTypeFor(fieldType: string): string {
  if (fieldType === 'email') return 'email';
  if (fieldType === 'mobile-number') return 'tel';
  return 'text';
}

/**
 * Lets the browser offer what it already knows. Only for the two fields where the value is
 * unambiguous — guessing on a free-text question would autofill an answer to the wrong question.
 */
function autoCompleteFor(fieldType: string): string | undefined {
  if (fieldType === 'email') return 'email';
  if (fieldType === 'mobile-number') return 'tel';
  return undefined;
}

// ── Controls ──────────────────────────────────────────────────────────────────

function ChoiceChips({
  options,
  selected,
  disabled,
  onToggle,
}: {
  options: string[];
  selected: string[];
  disabled: boolean;
  onToggle: (option: string) => void;
}) {
  if (options.length === 0) {
    return (
      <p className="text-[length:var(--text-12)] text-placeholder">
        No options configured for this question yet.
      </p>
    );
  }

  return (
    <div className="flex flex-wrap gap-2 pt-1">
      {options.map((option, index) => {
        const isSelected = selected.includes(option);
        return (
          <button
            // biome-ignore lint/suspicious/noArrayIndexKey: options are plain strings and duplicates occur in stored data — two chips with the same text must render as two chips, not collapse into one node. Position is the only identity available.
            key={`${option}-${index}`}
            type="button"
            disabled={disabled}
            onClick={() => onToggle(option)}
            aria-pressed={isSelected}
            className={cn(
              'cursor-pointer rounded-full border px-4 py-2 text-[13px] font-medium transition-all duration-150',
              isSelected
                ? 'border-primary bg-primary text-white shadow-xs'
                : 'border-[#CBD5E1] bg-white text-[#334155] hover:border-primary',
              disabled && 'cursor-default',
            )}
          >
            {option}
          </button>
        );
      })}
    </div>
  );
}

/**
 * A chain of dependent dropdowns — Country → State → District, however deep it was configured.
 *
 * ONE ANSWER, NOT ONE PER TIER. The whole chosen path is this field's single answer, which is what
 * makes it validatable as a unit (see `isBlank`) and what makes the stored record readable without
 * reassembling several keys.
 *
 * EVERY TIER IS VISIBLE FROM THE START, disabled until its turn.
 *
 * The earlier version revealed one select at a time, which hid how much the question actually asks:
 * a three-level address looked like a single "Country" dropdown, so the applicant answered it,
 * watched a second field appear, answered that, and got a third. Showing all three greyed out states
 * the shape of the question up front — and because the controls do not appear and disappear, nothing
 * below them jumps as the chain is filled in.
 *
 * A disabled tier still says what it is waiting for ("Select country first"), and a branch that
 * legitimately ends early says so ("No district options") rather than looking broken. Validation is
 * untouched: `reachableCascadeDepth` already only requires tiers that have options, so a shallow
 * branch stays completable.
 */
function CascadeSelects({
  cascade,
  answer,
  fieldLabel,
  isMandatory,
  disabled,
  onChange,
}: {
  cascade: Cascade;
  answer: CascadeAnswer;
  /** Fallback for the first tier's label, for a cascade whose Level 1 has no title yet. */
  fieldLabel: string;
  isMandatory: boolean;
  disabled: boolean;
  onChange: (value: CascadeAnswer) => void;
}) {
  const chosenIds = cascadePathIds(answer);

  /**
   * Choosing at `tier` TRUNCATES everything below it, because those answers belonged to the branch
   * that was just abandoned. Keeping them is how you end up storing Ahmedabad under Maharashtra.
   */
  function choose(tier: number, nodeId: string) {
    const level = cascade.levels[tier];
    if (!level) return;

    const kept = answer.path.slice(0, tier);

    if (nodeId.length === 0) {
      onChange({ path: kept });
      return;
    }

    const node = cascadeNodesAt(cascade, chosenIds.slice(0, tier)).find((n) => n.id === nodeId);
    if (!node) return;

    onChange({
      path: [...kept, { levelId: level.id, level: level.title, id: node.id, label: node.label }],
    });
  }

  // A cascade with no first-tier options has not been configured yet. Distinct from a tier that is
  // merely waiting on its parent, which is a normal state handled below.
  if (cascade.levels.length === 0 || cascade.nodes.length === 0) {
    return (
      <p className="text-[length:var(--text-12)] text-placeholder">
        No options configured for this question yet.
      </p>
    );
  }

  return (
    <div className="flex w-full flex-col gap-3 text-left">
      {cascade.levels.map((level, tier) => {
        const title = level.title.trim();
        // Options for this tier come from the path chosen ABOVE it. Empty means one of two very
        // different things, and the placeholder has to tell them apart.
        const options = cascadeNodesAt(cascade, chosenIds.slice(0, tier));
        const parentChosen = tier === 0 || answer.path[tier - 1] !== undefined;
        const parentTitle = cascade.levels[tier - 1]?.title.trim();

        const placeholder =
          options.length > 0
            ? level.placeholder || `Select ${title || 'an option'}`
            : parentChosen
              ? `No ${title || 'further'} options`
              : `Select ${parentTitle || 'the option above'} first`;

        // EVERY tier is labelled from its own level, first one included — the levels list in the
        // builder is the description of these dropdowns, so what is typed as Level 1 is what appears
        // above the first select. The field's title is only a fallback for a level left unnamed, so
        // an untitled Level 1 still reads as something rather than as "Level 1".
        const heading =
          title.length > 0
            ? title
            : tier === 0
              ? fieldLabel.trim() || 'Select an option'
              : 'Select an option';

        return (
          <div key={level.id} className="flex w-full flex-col gap-1.5 text-left">
            <span
              className={cn(
                'block text-[length:var(--text-14)] font-medium leading-snug transition-colors',
                options.length > 0 ? 'text-[#041A3A]' : 'text-[#94A3B8]',
              )}
            >
              {heading}
              {/* The marker belongs on the FIRST tier only: the whole chain is one answer, so one
                  asterisk describes it. Repeating it per select would read as three requirements. */}
              {tier === 0 && isMandatory ? (
                <span className="ml-0.5 text-destructive">*</span>
              ) : null}
            </span>
            <SelectMenu
              options={options.map((node) => ({ value: node.id, label: node.label }))}
              value={answer.path[tier]?.id ?? ''}
              placeholder={placeholder}
              // `SelectMenu` disables itself when there are no options, so this only has to carry
              // the caller's own read-only state.
              disabled={disabled}
              ariaLabel={title.length > 0 ? title : undefined}
              onChange={(nodeId) => choose(tier, nodeId)}
            />
          </div>
        );
      })}
    </div>
  );
}

/**
 * A single dropdown over a field's plain `options`.
 *
 * The label IS the value here, unlike a cascade tier which submits node ids so that relabelling an
 * option does not orphan answers already recorded against it.
 */
function PlainSelect({
  field,
  value,
  disabled,
  onChange,
}: {
  field: RenderableField;
  value: string;
  disabled: boolean;
  onChange: (value: string) => void;
}) {
  return (
    <SelectMenu
      options={optionsOf(field.config).map((option) => ({ value: option, label: option }))}
      value={value}
      // No option is selected by default, so an untouched dropdown reads as unanswered. Defaulting
      // to the first option — which the reference design does — silently submits "Gujarat" for
      // every applicant who never opened the list.
      placeholder={field.placeholder || 'Select an option'}
      disabled={disabled}
      onChange={onChange}
    />
  );
}

function DateInput({
  value,
  disabled,
  onChange,
  min,
  compact,
  placeholder,
  ariaLabel,
}: {
  value: string;
  disabled: boolean;
  onChange: (value: string) => void;
  min?: string;
  compact?: boolean;
  placeholder?: string;
  /** For the date-range halves, whose visible "From" / "To" text is not a wired-up label. */
  ariaLabel?: string;
}) {
  const inputRef = useRef<HTMLInputElement>(null);

  /**
   * Opens the native calendar from OUR icon.
   *
   * Needed because `.sc-date-input` hides the browser's own picker button — which was the only
   * thing that opened it. `showPicker()` is the standard call; older engines that lack it fall
   * back to focus + click, which is what they respond to.
   */
  function openPicker() {
    const el = inputRef.current;
    if (!el || disabled) return;
    try {
      el.showPicker();
    } catch {
      el.focus();
      el.click();
    }
  }

  return (
    <div className="relative w-full">
      <input
        ref={inputRef}
        type="date"
        // `sc-date-input` suppresses the BROWSER's calendar button. Without it this field showed
        // two icons: the native one and the designer's, side by side.
        className={cn(
          INPUT_CLASS,
          'sc-date-input cursor-pointer',
          compact ? 'pr-8 text-[12.5px]' : 'pr-10',
        )}
        value={value}
        min={min}
        disabled={disabled}
        placeholder={placeholder}
        aria-label={ariaLabel}
        onChange={(e) => onChange(e.target.value)}
      />
      {/* A real button, not a decorative glyph: it is now the only way to open the picker, so it
          has to be clickable and reachable. `tabIndex={-1}` keeps it out of the tab order — the
          input itself is already focusable and typing a date is the faster path. */}
      <button
        type="button"
        onClick={openPicker}
        disabled={disabled}
        tabIndex={-1}
        aria-hidden="true"
        className={cn(
          'absolute top-1/2 -translate-y-1/2 text-[#64748B] transition-colors hover:text-[#334155] disabled:cursor-not-allowed disabled:opacity-50',
          compact ? 'right-2.5' : 'right-3.5',
        )}
      >
        <Calendar size={compact ? 14 : 16} />
      </button>
    </div>
  );
}

/**
 * Largest total ONE Upload field may carry — across every file in it.
 *
 * PER FIELD, not per file and not per step. A field holding three files shares this budget, while a
 * second Upload field gets its own full 10 MB: two questions each asking for a document are two
 * independent asks, and making them compete would mean answering one could make another impossible.
 *
 * 10 MB is the industry norm for KYC document capture, where a phone photo of a card or a statement
 * PDF is typically 1–4 MB.
 *
 * MUST STAY IN STEP WITH THE SERVER: `SessionUploadLimits.MaxFieldBytes` enforces the same number,
 * because this check runs in a browser and the run submits over an ANONYMOUS route.
 */
const MAX_FIELD_BYTES = 10 * 1024 * 1024;

/**
 * Largest total across ALL upload fields of one step — a TRANSPORT BACKSTOP, not a product rule.
 *
 * Upload fields are independent: two of them each get their own {@link MAX_FIELD_BYTES}. But they
 * all travel in ONE request (a step submits every section together), so their sum still has to fit.
 * Four fields at 10 MB would be 40 MB, arriving as ~53 MB of base64 — past what a single JSON POST
 * should carry, and past Kestrel's default.
 *
 * 20 MB covers two fully-loaded fields, which is what a realistic KYC questionnaire has. Beyond
 * that the applicant is refused AT THE PICK, with the reason — not after filling in the whole form.
 *
 * Must match `SessionUploadLimits.MaxStepBytes`.
 */
const MAX_STEP_BYTES = 20 * 1024 * 1024;

/**
 * What an upload field accepts, enforced in code as well as advertised in `accept`.
 *
 * `accept` only filters what the OS dialog SHOWS — every file picker has an "All files" escape, so
 * without this check an applicant could attach a .zip and it would be stored and offloaded as
 * happily as a PDF.
 */
const ACCEPTED_UPLOAD_TYPES = ['image/png', 'image/jpeg', 'application/pdf'];

/**
 * How many attached files are shown before the list starts scrolling inside itself.
 *
 * Three, because the section also carries the picker, the summary line and whatever question comes
 * next — and on a phone the applicant needs Continue to stay reachable without hunting for it. It is
 * a display cap only; the real ceiling is {@link MAX_FIELD_BYTES}.
 */
const VISIBLE_FILE_ROWS = 3;

/**
 * Fixed row height, in px.
 *
 * FIXED rather than padding-derived so the `maxHeight` above can be computed exactly and the cut
 * lands between rows. With `py-*` the row height depends on the rendered font, which would make the
 * cap approximate and leave a sliver of a fourth row showing.
 */
const FILE_ROW_HEIGHT = 38;

/** "12.4 MB" — so a rejection can say how far over the limit things actually are. */
function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Reads one picked file as a data URI. Rejects rather than resolving null, so callers can await. */
function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () =>
      typeof reader.result === 'string'
        ? resolve(reader.result)
        : reject(new Error('Unreadable file'));
    reader.onerror = () => reject(reader.error ?? new Error('Unreadable file'));
    reader.readAsDataURL(file);
  });
}

function FileField({
  files,
  label,
  disabled,
  onChange,
  stepUploadBytes,
}: {
  files: UploadedFile[];
  label: string;
  disabled: boolean;
  onChange: (value: unknown) => void;
  /** Bytes attached across the WHOLE step, this field included. */
  stepUploadBytes: number;
}) {
  /**
   * Why the last pick was refused, or null.
   *
   * Local rather than part of `SectionErrors`: this is not a validation failure of the ANSWER (the
   * field may be optional and still hold nothing) — it is feedback about the files just chosen, and
   * it has to clear the moment a good one is picked.
   */
  const [pickError, setPickError] = useState<string | null>(null);

  const ownBytes = files.reduce((sum, f) => sum + uploadByteSize(f), 0);

  // What the OTHER upload fields in this step hold. Derived by subtracting this field's own files
  // from the step total — which is also what lets an applicant swap a 9 MB file for another 9 MB one
  // instead of being told the field is full.
  const otherBytes = Math.max(stepUploadBytes - ownBytes, 0);

  /**
   * How much more THIS field can take: the smaller of its own budget and what is left of the step's
   * transport backstop.
   *
   * The minimum matters. A field advertising "10 MB left" while the step has 2 MB of headroom would
   * invite a pick that then has to be refused — exactly the confusing failure this check exists to
   * remove.
   */
  const fieldRemaining = Math.max(MAX_FIELD_BYTES - ownBytes, 0);
  const stepRemaining = Math.max(MAX_STEP_BYTES - otherBytes - ownBytes, 0);
  const remaining = Math.min(fieldRemaining, stepRemaining);

  async function handlePick(event: ChangeEvent<HTMLInputElement>) {
    const picked = Array.from(event.target.files ?? []);
    // Reset immediately so re-picking the same file fires `change` again — without this, an
    // applicant who removes a file and re-selects it sees nothing happen.
    event.target.value = '';
    if (picked.length === 0) return;

    // Every rejection SAYS SO. These used to `onChange(null)` and return in silence, so choosing a
    // 30 MB file looked exactly like the picker having done nothing — the applicant had no way to
    // tell a refusal from a bug, and would try the same file again.
    const wrongType = picked.find((f) => !ACCEPTED_UPLOAD_TYPES.includes(f.type));
    if (wrongType) {
      setPickError(`"${wrongType.name}" isn't a PNG, JPG or PDF.`);
      return;
    }

    // Measured cumulatively, so picking three files at once is weighed together rather than each
    // against a total that has not moved yet. TWO running figures, because the two ceilings are
    // about different things — this field, and everything travelling in the same request.
    const accepted: UploadedFile[] = [];
    let fieldUsed = ownBytes;
    let stepUsed = otherBytes + ownBytes;
    let refusal: string | null = null;

    for (const file of picked) {
      // The FIELD's own budget first: it is the product rule, and the one an applicant can act on
      // by removing a file from this question. Reporting the step backstop when a single field is
      // over would send them looking in the wrong place.
      if (fieldUsed + file.size > MAX_FIELD_BYTES) {
        refusal =
          fieldUsed === 0
            ? `That file is ${formatFileSize(file.size)}. The limit is ${formatFileSize(MAX_FIELD_BYTES)} per question.`
            : `"${file.name}" would take this question over ${formatFileSize(MAX_FIELD_BYTES)} — ${formatFileSize(fieldUsed)} is already attached here.`;
        break;
      }

      // The step backstop second. Its wording names the WHOLE FORM, because every file involved is
      // legitimately under the field limit and "choose a smaller file" would be wrong advice.
      if (stepUsed + file.size > MAX_STEP_BYTES) {
        refusal =
          `"${file.name}" would take the whole form over ${formatFileSize(MAX_STEP_BYTES)}. ` +
          'Please remove a file from another question first.';
        break;
      }

      try {
        accepted.push({ name: file.name, dataUrl: await readAsDataUrl(file) });
      } catch {
        refusal = `"${file.name}" couldn't be read. Please try another file.`;
        break;
      }
      fieldUsed += file.size;
      stepUsed += file.size;
    }

    setPickError(refusal);

    // Whatever fit is KEPT. Refusing the whole batch because the last file was too big would throw
    // away files the applicant legitimately attached and make them select again.
    if (accepted.length > 0) {
      onChange([...files, ...accepted]);
    }
  }

  function removeAt(index: number) {
    const next = files.filter((_, i) => i !== index);
    // An empty list is stored as null, not [], so `isBlank` sees an unanswered field and a
    // mandatory upload is still refused at Continue.
    onChange(next.length > 0 ? next : null);
    setPickError(null);
  }

  return (
    <div className="flex flex-col gap-1.5">
      {/* A one-line summary, so the applicant can see what is attached and how much room is left
          WITHOUT scrolling the list. It only earns its place once the list is capped below. */}
      {files.length > VISIBLE_FILE_ROWS && (
        <div className="flex items-baseline justify-between text-[11px] text-placeholder">
          <span>
            {files.length} files · {formatFileSize(ownBytes)} used
          </span>
          <span>{formatFileSize(remaining)} left</span>
        </div>
      )}

      {/*
        THE LIST IS CAPPED, NOT ALLOWED TO GROW. Five attachments turned a short section into a page
        of scrolling and pushed Continue below the fold — the form's height changed depending on how
        many documents someone had, which is not something the layout should be at the mercy of.
        Beyond VISIBLE_FILE_ROWS the list scrolls INSIDE itself, so the section is a constant height
        whether there is one file or twenty.

        Deliberately NOT two-per-row: this card is phone width (~480px at most), where two columns
        would truncate "SignCare_Studio_Final_v2 (1).pdf" to something unrecognisable. A readable
        filename is the whole point of the row.

        `custom-scrollbar` is the thin scrollbar every other scrollable panel in the product uses —
        visible rather than hidden, because a hidden one makes the extra files undiscoverable.
      */}
      <div
        className={cn(
          'flex flex-col gap-1.5',
          files.length > VISIBLE_FILE_ROWS && 'custom-scrollbar overflow-y-auto pr-1',
        )}
        style={
          files.length > VISIBLE_FILE_ROWS
            ? // Height of the visible rows plus their gaps, so the cut lands cleanly BETWEEN rows —
              // a half-row peeking out reads as a rendering fault rather than as "scroll for more".
              { maxHeight: VISIBLE_FILE_ROWS * FILE_ROW_HEIGHT + (VISIBLE_FILE_ROWS - 1) * 6 }
            : undefined
        }
      >
        {files.map((file, index) => (
          <div
            // Keyed by position: two files can share a name, and nothing else here identifies one.
            // biome-ignore lint/suspicious/noArrayIndexKey: position is the only identity a file list has.
            key={index}
            className="flex shrink-0 items-center gap-2 rounded-lg border border-placeholder bg-white px-2.5"
            style={{ height: FILE_ROW_HEIGHT }}
          >
            <UploadIcon size={13} className="shrink-0 text-primary" />
            <span className="min-w-0 flex-1 truncate text-[12px] text-[#323338]">{file.name}</span>
            <span className="shrink-0 text-[10px] text-placeholder">
              {formatFileSize(uploadByteSize(file))}
            </span>
            {!disabled && (
              <button
                type="button"
                onClick={() => removeAt(index)}
                aria-label={`Remove ${file.name}`}
                className="shrink-0 text-[#94A3B8] transition-colors hover:text-destructive"
              >
                <X size={14} />
              </button>
            )}
          </div>
        ))}
      </div>

      {/* The picker STAYS after the first file — that is what makes the field multi-file — and
          disappears only once the budget is spent, so it never offers an action that must then be
          refused. */}
      {!disabled && remaining > 0 && (
        <label
          className={cn(
            'flex cursor-pointer items-center justify-center gap-1.5 rounded-lg border border-dashed bg-white',
            // TWO SIZES. Empty, it is the field's main affordance and gets the tall two-line box the
            // designer drew. Once files are listed it collapses to ONE compact line — the tall box
            // was then a second large empty area under a list that already explains itself, and it
            // was a good part of why five attachments ran off the screen.
            files.length === 0 ? 'flex-col p-3' : 'px-2.5 py-2',
            // The dashed border turns red on a refusal, so the box itself shows something happened
            // even if the message below is scrolled out of view on a short phone.
            pickError ? 'border-destructive' : 'border-placeholder',
          )}
          style={files.length > 0 ? { minHeight: FILE_ROW_HEIGHT } : undefined}
        >
          <span className="flex items-center gap-1.5 text-[12px] font-semibold text-primary">
            <UploadIcon size={13} />
            {files.length > 0 ? 'Add another file' : label}
          </span>
          {/* The hint shows what is LEFT once anything is attached anywhere in the step — `remaining`
              is already the minimum of this field's budget and the form's headroom, so it is always
              the number governing the next pick.

              Suppressed once the summary line above is showing it, rather than stating the same
              figure twice in adjacent rows. */}
          {files.length <= VISIBLE_FILE_ROWS && (
            <span className="text-[10px] text-placeholder">
              {files.length > 0 || otherBytes > 0
                ? `PNG, JPG, PDF — ${formatFileSize(remaining)} left`
                : `PNG, JPG, PDF up to ${formatFileSize(MAX_FIELD_BYTES)}`}
            </span>
          )}
          <input
            type="file"
            multiple
            accept={ACCEPTED_UPLOAD_TYPES.join(',')}
            className="hidden"
            onChange={handlePick}
          />
        </label>
      )}

      {pickError && <span className="text-[11px] text-destructive">{pickError}</span>}
    </div>
  );
}
